import React, { useState } from 'react';
import { Calendar, CalendarDays, MapPin, Users, Plus, Bell, Settings, LogOut, Menu, X, BarChart3, Trophy, Download, ChevronDown, Wrench, AlertCircle, MessageSquare, DollarSign } from 'lucide-react';
import { log } from '../../../utils/logger';
import { useUnreadNotificationCount } from '../../../queries/notificationQueries';

const NavBar = ({
  currentUser,
  mobileMenuOpen,
  setMobileMenuOpen,
  preservedReservationState,
  setPreservedReservationState,
  reservationSuccess,
  setReservationSuccess,
  isDesktop,
  handleLogout,
  activeTab,
  setActiveTab
}) => {
  const tabs = {
    teamrezy_employee: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'venues', label: 'Venues', icon: MapPin },
      { id: 'games', label: 'Games', icon: Trophy },
      { id: 'tournament_games', label: 'Tournament Games', icon: CalendarDays },
      { id: 'users', label: 'Users', icon: Users },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'adminPanel', label: 'Payments', icon: DollarSign },
      { id: 'contact', label: 'Contact Messages', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager_primary: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    team_manager_assistant: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'teams', label: 'Teams', icon: Users },
      { id: 'tournaments', label: 'Tournaments', icon: Trophy },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'book', label: 'Add Reservation', icon: Plus },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ],
    venue_manager: [
      { id: 'overview', label: 'Overview', icon: BarChart3 },
      { id: 'reservations', label: 'Reservations', icon: Calendar },
      { id: 'venues', label: 'My Venue', icon: MapPin },
      { id: 'contact', label: 'Contact Us', icon: MessageSquare },
      { id: 'account', label: 'Account', icon: Settings }
    ]
  };

  // Tools dropdown items (only for test users)
  const toolsItems = currentUser?.is_test_user ? (
    currentUser?.role === 'teamrezy_employee' ? [
      { id: 'authDiagnostic', label: 'Auth Diagnostic', icon: AlertCircle },
      { id: 'hockeyFinderImport', label: 'Hockey Finder Import', icon: Download }
    ] : [
      { id: 'authDiagnostic', label: 'Auth Diagnostic', icon: AlertCircle }
    ]
  ) : [];

  const [showToolsDropdown, setShowToolsDropdown] = useState(false);

  // Get unread notification count
  const { data: unreadCount = 0 } = useUnreadNotificationCount();

  const handleTabClick = (tabId) => {
    // Clear preserved state when navigating away from reservations
    if (activeTab === 'book' && tabId !== 'book' && preservedReservationState) {
      log.info('🏨 Clearing preserved state on tab navigation from reservations');
      setPreservedReservationState(null);
    }

    // Clear success message when navigating to "book" tab
    if (tabId === 'book') {
      if (reservationSuccess) {
        log.info('🎉 Clearing success message when navigating to book tab from:', activeTab);
        setReservationSuccess(null);
      }
    }

    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="nav-container">
        {/* Left section - Logo and Welcome */}
        <div className="nav-left">
          <img
            src="/TeamRezyLogo.png"
            alt="TeamRezy Logo"
            className="h-20 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity duration-200"
            onClick={() => setActiveTab('overview')}
            title="Go to Overview"
          />
          {isDesktop && window.innerWidth >= 1024 && (
            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">
              {currentUser
                ? `Welcome, ${currentUser.first_name || currentUser.email}`
                : ''}
            </span>
          )}
        </div>

        {/* Center section - Navigation Tabs (Desktop only) */}
        <div className="nav-center">
          {isDesktop && window.innerWidth >= 1024 && (
            <>
              {tabs[currentUser?.role]?.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'text-navy-600 border-navy-600'
                      : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                  }`}
                  style={{
                    border: 'none',
                    borderBottom: activeTab === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                    marginLeft: index > 0 ? '1rem' : '0'
                  }}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              ))}

              {/* Tools Dropdown */}
              {toolsItems.length > 0 && (
                <div className="relative" style={{ marginLeft: '1rem' }}>
                  <button
                    onClick={() => setShowToolsDropdown(!showToolsDropdown)}
                    onBlur={() => setTimeout(() => setShowToolsDropdown(false), 200)}
                    className={`flex items-center space-x-2 py-4 px-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors duration-200 ${
                      toolsItems.some(item => activeTab === item.id)
                        ? 'text-navy-600 border-navy-600'
                        : 'text-gray-500 hover:text-gray-700 border-transparent hover:border-gray-300'
                    }`}
                    style={{
                      border: 'none',
                      borderBottom: toolsItems.some(item => activeTab === item.id) ? '2px solid #2563eb' : '2px solid transparent'
                    }}
                  >
                    <Wrench className="w-4 h-4" />
                    <span>Tools</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showToolsDropdown ? 'rotate-180' : ''}`} />
                  </button>

                  {showToolsDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                      {toolsItems.map((item, index) => {
                        if (item.label === 'separator') {
                          return (
                            <div key={item.id} className="border-t border-gray-200 my-1"></div>
                          );
                        }
                        return (
                          <button
                            key={item.id}
                            onClick={() => {
                              handleTabClick(item.id);
                              setShowToolsDropdown(false);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors ${
                              index === 0 ? 'rounded-t-md' : ''
                            } ${
                              index === toolsItems.length - 1 ? 'rounded-b-md' : ''
                            } ${
                              activeTab === item.id ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                            }`}
                          >
                            <item.icon className="w-4 h-4" />
                            <span>{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Right section - Notifications and Logout (Desktop) / Mobile menu button */}
        <div className="nav-right">
          {isDesktop && window.innerWidth >= 1024 ? (
            <>
              <div className="relative">
                <button
                  onClick={() => handleTabClick('notifications')}
                  className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
                  title="Notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                title="Logout"
                style={{ border: 'none' }}
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </>
          ) : (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              className="mobile-menu-button p-2 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors duration-200"
              title={mobileMenuOpen ? "Close menu" : "Open menu"}
              style={{ border: 'none' }}
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
